import type { ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Calendar, BarChart3, User, Sparkles, LogOut, Users } from "lucide-react";

import { signOut } from "@/lib/auth";
import { hasRole, useAuthUser } from "@/lib/use-auth-user";
import { Logo } from "@/components/ui/Logo";
import { GlobalFooter } from "./GlobalFooter";
import { cn } from "@/lib/utils";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

const BASE_NAV: ReadonlyArray<NavItem> = [
  { to: "/events", label: "My Events", icon: Calendar },
  { to: "/events/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/account", label: "Account", icon: User },
  { to: "/pricing", label: "Pricing", icon: Sparkles },
];

// Extra entry shown only to users who also hold the `participant` role —
// gives an organizer a one-tap way to jump into their participant inbox
// (matches, events they're attending). Without it the only way to leave
// the organizer area is to manually type /me/meetups in the URL.
const PARTICIPANT_NAV: NavItem = {
  to: "/me/meetups",
  label: "My meetups",
  icon: Users,
};

/**
 * Documentation §11.1 — single nav panel. Sidebar on desktop, bottom tabbar
 * on mobile. Same menu, same actions.
 */
export function OrganizerShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const nav: ReadonlyArray<NavItem> = hasRole(user, "participant")
    ? [BASE_NAV[0], BASE_NAV[1], PARTICIPANT_NAV, BASE_NAV[2], BASE_NAV[3]]
    : BASE_NAV;

  const handleSignOut = async () => {
    await signOut();
    navigate("/sign-in");
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#fafafa]">
      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <aside className="hidden w-[240px] shrink-0 flex-col border-r border-[#e2e8f0] bg-white px-5 py-6 lg:flex">
          <Logo size="sm" to="/events" />
          <nav className="mt-8 flex flex-1 flex-col gap-1">
            {nav.map((item) => (
              <SidebarLink key={item.to} {...item} />
            ))}
          </nav>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex items-center gap-2 rounded-[10px] px-3 py-2 text-[13px] font-semibold text-[#475569] transition-colors hover:bg-[#f8fafc] hover:text-[#1a1a1a]"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </aside>

        {/* Mobile top bar */}
        <header className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-[#e2e8f0] bg-white px-4 lg:hidden">
          <Link to="/events" className="min-w-0 no-underline">
            <Logo size="sm" />
          </Link>
          <button
            type="button"
            onClick={handleSignOut}
            className="shrink-0 text-[13px] font-semibold text-[#6c757d] hover:text-[#1a1a1a]"
          >
            Sign out
          </button>
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden pt-14 pb-20 lg:pt-0 lg:pb-0">
          <div className="mx-auto w-full max-w-[1080px] px-4 py-6 sm:px-8 sm:py-10">{children}</div>
        </main>
      </div>

      {/* Mobile bottom tabbar */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-[#e2e8f0] bg-white lg:hidden">
        {nav.map((item) => (
          <TabLink key={item.to} {...item} />
        ))}
      </nav>

      <GlobalFooter />
    </div>
  );
}

function SidebarLink({
  to,
  label,
  icon: Icon,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <NavLink
      to={to}
      end={to === "/events"}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-[14px] font-semibold transition-colors no-underline",
          isActive
            ? "bg-[#e6f1fb] text-[#2563eb]"
            : "text-[#475569] hover:bg-[#f8fafc] hover:text-[#1a1a1a]",
        )
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}

function TabLink({
  to,
  label,
  icon: Icon,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <NavLink
      to={to}
      end={to === "/events"}
      className={({ isActive }) =>
        cn(
          "flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold no-underline",
          isActive ? "text-[#2563eb]" : "text-[#94a3b8]",
        )
      }
    >
      <Icon className="h-5 w-5" />
      {label}
    </NavLink>
  );
}
